import { useEffect } from 'react';
import { getCurrentVersion } from '../api/api';
import { showNotification } from '../components/Notification';
import { trackComponentInteraction } from '../utils/analytics';

const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_RELOAD_DELAY_MS = 1500;
const RELOAD_ATTEMPT_KEY = 'coopgpt-app-version-reload-attempt';

interface UseAppVersionCheckOptions {
  isAuthenticated: boolean;
  intervalMs?: number;
  reloadDelayMs?: number;
  onReload?: (latestVersion: string) => void;
  runInDev?: boolean;
}

export function normalizeAppVersion(version: string | undefined | null): string {
  return (version || '').trim().replace(/^['"]|['"]$/g, '');
}

export function getBuiltAppVersion(): string {
  return normalizeAppVersion(import.meta.env.VITE_APP_VERSION);
}

export function isAppVersionMismatch(currentVersion: string, latestVersion: string): boolean {
  const current = normalizeAppVersion(currentVersion);
  const latest = normalizeAppVersion(latestVersion);
  return Boolean(current && latest && current !== latest);
}

export function getCacheBustedUrl(latestVersion: string, href = window.location.href): string {
  const url = new URL(href);
  url.searchParams.set('appVersion', normalizeAppVersion(latestVersion));
  url.searchParams.set('cacheBust', Date.now().toString());
  return url.toString();
}

function getReloadAttemptedVersion(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
  } catch {
    return null;
  }
}

function setReloadAttemptedVersion(latestVersion: string): void {
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, normalizeAppVersion(latestVersion));
  } catch {
    // Ignore storage failures and still attempt the reload.
  }
}

function clearReloadAttemptedVersion(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function reloadWithCacheBuster(latestVersion: string): void {
  setReloadAttemptedVersion(latestVersion);
  window.location.replace(getCacheBustedUrl(latestVersion));
}

export function useAppVersionCheck({
  isAuthenticated,
  intervalMs = DEFAULT_CHECK_INTERVAL_MS,
  reloadDelayMs = DEFAULT_RELOAD_DELAY_MS,
  onReload = reloadWithCacheBuster,
  runInDev = false,
}: UseAppVersionCheckOptions): void {
  useEffect(() => {
    if (!isAuthenticated || (!runInDev && import.meta.env.DEV)) {
      return undefined;
    }

    let cancelled = false;
    let checkInFlight = false;
    let mismatchHandled = false;
    let reloadTimer: number | undefined;

    const checkVersion = async () => {
      if (cancelled || checkInFlight || mismatchHandled) {
        return;
      }

      checkInFlight = true;
      try {
        const currentVersion = getBuiltAppVersion();
        const latestVersion = normalizeAppVersion(await getCurrentVersion());

        if (!isAppVersionMismatch(currentVersion, latestVersion)) {
          clearReloadAttemptedVersion();
          return;
        }

        mismatchHandled = true;
        trackComponentInteraction(
          'app-version-checker',
          'App Version Checker',
          'system',
          'version_mismatch',
          `${currentVersion}->${latestVersion}`
        );

        if (getReloadAttemptedVersion() === latestVersion) {
          showNotification('A new version is available. Refresh this tab to update.');
          return;
        }

        setReloadAttemptedVersion(latestVersion);
        showNotification('New version available. Refreshing...');
        reloadTimer = window.setTimeout(() => {
          if (!cancelled) {
            onReload(latestVersion);
          }
        }, reloadDelayMs);
      } catch (error) {
        console.warn('Failed to check app version:', error);
      } finally {
        checkInFlight = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    void checkVersion();
    const intervalId = window.setInterval(checkVersion, intervalMs);
    window.addEventListener('focus', checkVersion);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkVersion);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (reloadTimer) {
        window.clearTimeout(reloadTimer);
      }
    };
  }, [isAuthenticated, intervalMs, onReload, reloadDelayMs, runInDev]);
}
